import React, { useMemo, useState, useEffect } from "react";
import Grid from "@mui/material/Grid2";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Badge,
  CircularProgress,
  Divider,
  TablePagination,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ListEmpty from "../GeneralComponents/ListEmpty";
import InputFilter from "../GeneralComponents/InputFilter";
import InputChipSelect from "../GeneralComponents/InputChipSelect";

export default function GenericList({
  data = [],
  loading = false,
  pageTitleOverride = "Bajas por operador",
  dateField = "fecha_baja",
  totalField = "total_bajas",          
  totalColumnLabel = "Total bajas",  
  badgeChipLabel = "Bajas",   
}) {
  const [filters, setFilters] = useState({});

  // Opciones de meses ordenadas cronológicamente
  const monthsOptions = useMemo(() => {
    const seen = new Map();
    for (const row of data) {
      const orden = row?.mes_orden;
      if (!orden) continue;
      const nombreMes = (row.mes || "").split(" ")[0] || "";
      const label = `${orden} (${nombreMes})`;
      if (!seen.has(orden)) seen.set(orden, label);
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, label]) => label);
  }, [data]);

  // Filas planas para filtros
  const rowsForFilters = useMemo(() => {
    const flat = [];
    for (const row of data) {
      const orden = row?.mes_orden;
      if (!orden) continue;
      const nombreMes = (row.mes || "").split(" ")[0] || "";
      const mes_label = `${orden} (${nombreMes})`;

      flat.push({
        mes: row.mes,
        mes_orden: orden,
        mes_label,
        operador: row.operador,
      });

      (row.rbds || []).forEach((r) => {
        const rbd = r?.rbd;
        if (rbd != null && rbd !== "") {
          flat.push({
            mes: row.mes,
            mes_orden: orden,
            mes_label,
            operador: row.operador,
            rbd: String(rbd),
          });
        }
      });
    }
    return flat;
  }, [data]);

  const filterRecords = (name, value) => {
    const next = { ...filters, [name]: value };
    const isEmpty = value === null || (Array.isArray(value) && value.length === 0);
    if (isEmpty) delete next[name];
    setFilters(next);
  };


  const filtrado = useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) return data;

    return data.filter((row) => {
      const orden = row?.mes_orden;
      const nombreMes = (row.mes || "").split(" ")[0] || "";
      const rowMesLabel = `${orden} (${nombreMes})`;

      if (filters.mes_label && rowMesLabel !== filters.mes_label) return false;
      if (filters.operador && row.operador !== filters.operador) return false;

      if (filters.rbd && Array.isArray(filters.rbd) && filters.rbd.length > 0) {
        const setRbd = new Set(filters.rbd.map(String));
        const has = (row.rbds || []).some((r) => setRbd.has(String(r?.rbd)));
        if (!has) return false;
      }

      return true;
    });
  }, [data, filters]);

  const gruposPorMes = useMemo(() => {
    const g = new Map();
    for (const row of filtrado) {
      const orden = row?.mes_orden;
      if (!orden) continue;

      if (!g.has(orden)) {
        g.set(orden, {
          mes: row.mes,
          mes_orden: orden,
          items: [],
          total_operadores: 0,
          total_metric: 0,
        });
      }
      const bucket = g.get(orden);
      bucket.items.push(row);
      bucket.total_operadores += 1;
      bucket.total_metric += Number(row?.[totalField] ?? 0);
    }
    return Array.from(g.values()).sort((a, b) => a.mes_orden.localeCompare(b.mes_orden));
  }, [filtrado, totalField]);

  // Paginación (meses)
  const [pageMes, setPageMes] = useState(0);
  const [rowsPerPageMes, setRowsPerPageMes] = useState(10);

  useEffect(() => {
    const pages = Math.ceil(gruposPorMes.length / rowsPerPageMes);
    const maxPage = Math.max(pages - 1, 0);
    if (pageMes > maxPage) setPageMes(0);
  }, [gruposPorMes.length, rowsPerPageMes, pageMes]);

  const visibleMeses = useMemo(() => {
    const start = pageMes * rowsPerPageMes;
    return gruposPorMes.slice(start, start + rowsPerPageMes);
  }, [gruposPorMes, pageMes, rowsPerPageMes]);

  return (
    <>
      {/* Filtros */}
      <Grid container spacing={1} sx={{ marginBottom: "28px", marginTop: "-2rem" }} alignItems="flex-start">
        <Grid size={6}>
          <Grid container spacing={1}>
            <Grid size={6}>
              <InputFilter
                data={rowsForFilters}
                item={"mes_label"}
                name={"Mes"}
                opt={monthsOptions}
                callback={filterRecords}
              />
            </Grid>

            <Grid size={6}>
              <InputFilter
                data={rowsForFilters}
                item={"operador"}
                name={"Operador"}
                callback={filterRecords}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container size={6} spacing={0}>
          <Grid size={12}>
            <InputChipSelect
              data={rowsForFilters}
              item={"rbd"}
              name={"RBD"}
              callback={filterRecords}
            />
          </Grid>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ mb: 2, p: 1, textAlign: "center" }}>
            <Typography variant="h1" noWrap sx={{ textAlign: "center", padding: "0.5rem" }}>
              {pageTitleOverride}
            </Typography>
          </Paper>

          {/* Lista de acordeones paginada */}
          {gruposPorMes.length === 0 ? (
            <ListEmpty />
          ) : (
            <>
              <Stack spacing={3}>
                {visibleMeses.map((mes) => (
                  <Accordion key={mes.mes_orden} defaultExpanded disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ width: "100%", justifyContent: "space-between" }}
                      >
                        <Typography variant="subtitle1" fontWeight={700}>
                          {mes.mes?.toUpperCase?.() || mes.mes}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Badge color="primary" badgeContent={mes.total_operadores} max={999}>
                            <Chip label="Operadores" size="small" />
                          </Badge>
                          <Badge color="secondary" badgeContent={mes.total_metric} max={999}>
                            <Chip label={badgeChipLabel} size="small" />
                          </Badge>
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <OperadoresTableNoDates
                        rows={mes.items}
                        dateField={dateField}
                        totalField={totalField}
                        totalColumnLabel={totalColumnLabel}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>

              {/* Paginación de acordeones */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  count={gruposPorMes.length}
                  rowsPerPage={rowsPerPageMes}
                  page={pageMes}
                  onPageChange={(_, p) => setPageMes(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPageMes(parseInt(e.target.value, 10));
                    setPageMes(0);
                  }}
                  labelRowsPerPage="Meses por página:"
                />
              </Box>
            </>
          )}
        </>
      )}
    </>
  );
}

function OperadoresTableNoDates({
  rows,
  dateField = "fecha_baja",
  totalField = "total_bajas",
  totalColumnLabel = "Total bajas",
}) {
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) =>
      String(a.operador || "").localeCompare(String(b.operador || ""), undefined, {
        sensitivity: "base",
      })
    );
  }, [rows]);

  const groupByDate = (rbds = []) => {
    const map = new Map();
    rbds.forEach((r) => {
      const rbd = r?.rbd;
      if (rbd == null || rbd === "") return;
      const k = (r && r[dateField]) || ""; 
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(String(rbd));
    });
    for (const [k, arr] of map) arr.sort((a, b) => a.localeCompare(b));
    return Array.from(map.entries()).sort((a, b) => (a[0] || "").localeCompare(b[0] || ""));
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Operador</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, width: 140, whiteSpace: "nowrap" }}>
              {totalColumnLabel}
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}>RBDs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row) => {
            const byDate = groupByDate(row.rbds);

            return (
              <TableRow key={`${row.mes_orden}-${row.operador}`} hover>
                <TableCell width={280}>
                  <Typography variant="body2" fontWeight={700}>{row.operador}</Typography>
                </TableCell>

                <TableCell align="right" width={140}>
                  <Chip label={row?.[totalField] ?? 0} size="small" />
                </TableCell>

                <TableCell sx={{ verticalAlign: "top" }}>
                  <Stack spacing={1.25}>
                    {byDate.length === 0 && (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                    {byDate.map(([date, list], idx) => (
                      <Box key={date || `sindate-${idx}`}>
                        <Typography variant="overline" sx={{ lineHeight: 1, fontWeight: 500 }}>
                          {date ? fmtFecha(date) : "Sin fecha"} ({list.length})
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 0.5 }}>
                          {list.map((rbd) => (
                            <Chip key={`${date}-${rbd}`} label={rbd} size="small" variant="outlined" />
                          ))}
                        </Stack>
                        {idx !== byDate.length - 1 && <Divider sx={{ my: 1 }} />}
                      </Box>
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function fmtFecha(s) {
  if (!s) return "";
  const [y, m, d] = String(s).split("-");
  if (!y || !m || !d) return s;
  return `${d}-${m}-${y}`;
}
